/**
 * @module  experience/hardgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:max-file-line-count */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { filter, takeUntil, debounceTime } from 'rxjs/operators';
import { Location } from '@angular/common';

// Models and Interfaces
import { EntryModal } from '@experience/shop/shared-browse/models/entry-modal.model';

// Services
import { ScrollListenerService } from '@support/shared/services/scroll-listener.service';
import { BrowseHardgoodsService } from '@experience/shop/hardgoods/services/browse-hardgoods.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { UserService } from '@support/shared/services/user.service';

// Interfaces
import {
  SelectedProductInfo,
  BrowsePhonesQueryParams,
  HardgoodsDeviceDetails,
  DeviceProductOfferingIdSearchResults,
  StoredFilter,
  ProductDescription,
  CurrentDeviceDetails,
} from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
import {
  HardgoodOffer,
  SortRecord,
} from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
import {
  Filter,
  SortOptions,
  FilterOption,
  UserDisplayFilter,
  Product,
} from '@support/api/common/product.interface';
import {
  HardgoodsContent,
  FilterCriteria,
} from '@core/aem/interfaces/shop/browse-hardgoods.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';

// Constants
import { TEXT_CONSTANTS } from '@support/shared/constants/device.constants';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';

import {
  HARDGOODS_CONSTANTS,
  HARDGOODS_INFINITE_SCROLL_CONSTANTS,
} from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { HARDGOODS_MODULE_COMPONENTS } from '@experience/shop/hardgoods/constants/hardgoods.constants';
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { LoggerService } from '@core/logger/services/logger.service';
import { AutoCompleteMapping } from '@support/shared/widgets/interfaces/autocomplete.interface';
import { Subject } from 'rxjs';
import { APIError } from '@support/api/common/error.interface';
import { SETTINGS, CHANNELS } from '@environment/settings';
import { BrowseDeviceModel } from '@experience/shop/hardgoods/models/browse-device.model';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { WindowRefService } from '@support/shared/services/window-ref.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';

import { RepdashWindowRef } from '@experience/repdash/interfaces/repdash.interface';
import { AemAuthorableData } from '@core/aem/interfaces/legal-text/aem-authorable.interface';
import { GlobalErrorService } from '@support/shared/widgets/services/global-error.service';
import { RetailBrowseHardgoodComponent } from '../retail-browse-hardgood/retail-browse-hardgood.component';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { ProductDetailsService } from '@experience/shop/services/product-details.service';

@Component({
  selector: 'tmo-browse-devices-landing',
  templateUrl: './browse-devices-landing.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../../style-sheets/components/html-spinner.component.scss',
    '../../../../../../style-sheets/components/browse-device-accordian.component.scss',
    '../../../../../../style-sheets/components/input-toggle.component.scss',
  ],
})
export class BrowseDevicesLandingComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * Stores the sort options
   */
  public sorts: SortOptions[];
  /**
   * Stores the selected sort options , set to empty string for reset
   */
  public selectedSort: string;
  public filters: Filter[];
  /**
   * Stores the static modal for the modal data
   */
  public modalData: EntryModal;

  /**
   * Boolean flag to check if hardgoods have been loaded
   */
  public isLoading: boolean;

  /**
   * disableCheckbox is used to disable checkbox
   */
  public disableCheckbox: boolean;

  /**
   * disableCompareLink is used to disable compare link
   */
  public disableCompareLink: boolean;

  /**
   * Array of all hardgoods model
   */
  public hardgoods: HardgoodOffer[];

  /**
   * Checks if the entry config data has been initialized
   */
  public configDataLoaded: boolean;

  /**
   * Object Hardgoods configurable data
   */
  public contentData: HardgoodsContent;

  /**
   * String to store credit level
   */
  public selectedCreditLevel: string;
  /**
   * Boolean to track whether to show modal
   */
  public showModal: boolean;
  /**
   * Boolean to apply sort
   */
  public sortApplied: boolean;
  /**
   * Boolean to apply filter
   */
  public isFilterApplied: boolean;
  /**
   * Boolean to sort with criteria
   */
  public sortCriterion: string;
  /**
   * Boolean to show sorted elements
   */
  public sortDisplay: string;
  /**
   * Boolean to apply filter with criteria
   */
  public filterCriterion: string;
  /**
   * filter array to store filter categories
   */
  public filterCategories: Filter[];
  /**
   * filter criteria array to store previous selected values
   */
  public previousSelectedValues: FilterCriteria[];

  /**
   * Customer is eip eligible if credit is excellent or good.
   *
   * @type {boolean}
   * @memberof BrowseHardgoodComponent
   */
  public eipEligible: boolean;
  /**
   * Boolean to check if data is filled
   */
  private isDataFilled: boolean;
  /**
   * Boolean to get query Params for isLineInfocus
   */
  public isLineInFocus: boolean;
  /**
   * Boolean to check whether is Upgrade flow from query Params
   */
  public isUpgrade: boolean;
  /**
   * Boolean to apply clear filter criteria
   */
  private isClearSortFilter: boolean;
  /**
   *   Un-subscriber for all subscriptions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Did the user submit customer config data from the modal
   */
  private modalSubmitted: boolean;
  /**
   * constants
   */
  private hardgoodConstants: ConfigData;

  public hardGoods: BrowseDeviceModel[];
  /**
   * Holds class name depend on channel
   */
  public className: string;
  /**
   * viewport lenghts from the bottom of the page at which the event will be triggered.
   */
  public scrollDistance: Number;
  /**
   * the event will be triggered this many milliseconds after the user stops scrolling.
   */
  public throttle: Number;
  /**
   * Contain page number to send in request payload while making 2100 api call
   */
  public pageNumber: number;
  /**
   * stores boolean value if channel is web
   */
  // public channelWeb: boolean;

  /**
   * stores boolean value for accessory
   */
  public isAccessory: boolean;
  /**
   * Stores boolean value of scroll to be disabled
   */
  public disableScroll: boolean;
  /**
   * Holds to show component level spinner while making 2100 from second Time.
   */
  public showComponentSpinner: boolean;
  /**
   * Stores container for infinite scroll
   */
  public infiniteScrollContainer: Element;
  /**
   * Stores complete window for infinite scroll
   */
  public windowContainer: Window;

  public resultsCount: number;
  /**
   * Stores boolean if user is editing a line in upgrade flow
   */
  public isEditingUpgradeLine: boolean;
  /**
   * Stores boolean if user is editing a non line in upgrade flow
   */
  public isEditingNonLineDevice: boolean;
  /**
   * Stores user selected filters to be displayed
   */
  public userDisplayFilter: UserDisplayFilter[];
  /*
   * Stores user selected category filter
   */
  public errorMessage: APIError[];
  /**
   * Stores category filters to be displayed
   */
  public catagoryUserDisplayFilters: string[];
  /**
   * Stores page heading
   */
  public pageHeading: string;
  /**
   * Form control to enter device for searching compatible accessories
   */
  public findCompatibleAccessoriesFormControl: UntypedFormControl;
  /**
   * Map to store product offering ID and product name
   */
  public searchResultsForDeviceProductOfferingId: Map<string, string>;
  /**
   * Input for the auto complete mapping
   */
  public deviceSearchResultsAutoCompleteInput: AutoCompleteMapping[];
  /**
   * Device product offeringID for the item for which we will be searching compatible accessory
   */
  public selectedDeviceForCompatibleAccessories: string;
  /**
   * category selected for accessories
   */
  public selectedCategory: string;
  /**
   * Boolean to check if it ACDP page
   */
  public isACDP: boolean;
  /**
   * Boolean to show panel
   */
  public priceRangeValue: string;
  /**
   * Variable to store hardGoods response
   */
  public hardGoodsResponse: HardgoodsDeviceDetails;
  /**
   * Repdash window
   */
  public repDashwindow: RepdashWindowRef;

  /**
   * Stores value of legal text authorable filtered object
   */
  public legalText: AemAuthorableData[];
  /**
   * AEM Authorable Content for BYOD Landing Page
   */
  public authorableData: AemAuthorableData[];

  /** stoes the current number of categories loaded on the page
   */
  public currentCategoriesOnPage: number;

  /** stores  whether to add previous category data or not
   */
  public addPreviousCategoryData: boolean;

  /** stoes the current url of the page
   */
  public navigationUrl: string;
  /**
   * holds boolean for retail channel
   */
  public isRetail: boolean;

  /**
   * variable to show product description
   */
  public deviceDescription: CurrentDeviceDetails;
  /**
   * holds boolean to show hide devices
   */
  public showDevices: boolean;
  /**
   * reference of child component
   */
  @ViewChild(RetailBrowseHardgoodComponent, { static: false })
  public retailBrowsePlansComponent: RetailBrowseHardgoodComponent;
  /**
   * array of boolean values to store toggle details value
   */
  public toggleDetails: boolean[];
  /**
   * variable to store device index
   */
  public deviceIndex: number;
  /**
   * variable to store row index number
   */
  public deviceRowIndex: number;
  /**
   * variable to show updated browse plan
   */
  // public showUpdatedBrowseDevices: boolean;
  /**
   * stores value of store status
   */
  public isStoreOpen: boolean;
  /**
   * stores value of params if gloabal search
   */
  public isGlobalSearch: boolean;
  public bannerAdDesc: string;
  /**
   * holds value of p10m flag
   */
  public isP10MCustomer: boolean;

  public isAuthenticated: boolean;

  constructor(
    private scrollListenerService: ScrollListenerService,
    private globalErrorService: GlobalErrorService,
    private sharedBrowseService: SharedBrowseService,
    private browseHardgoodsService: BrowseHardgoodsService,
    private commonUtilService: CommonUtilService,
    private windowRefService: WindowRefService,
    public featureToggleUtilService: FeatureToggleUtilService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private logger: LoggerService,
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private productDetailService: ProductDetailsService,
    private userService: UserService,
    public pdlService: PdlService
  ) {
    super(pdlService, route);
    this.sortApplied =
      this.isFilterApplied =
      this.isClearSortFilter =
      this.isDataFilled =
      this.disableScroll =
        false;
    this.sortCriterion = this.sortDisplay = this.filterCriterion = '';
    this.filterCategories =
      this.hardGoods =
      this.previousSelectedValues =
      this.userDisplayFilter =
        [];
    this.modalSubmitted = false;
    this.addPreviousCategoryData = true;
    this.ngUnsubscribe = new Subject<void>();
    this.isAccessory = this.router.url.indexOf(HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES) > -1;
    this.hardgoodConstants = HARDGOODS_CONSTANTS;
    this.scrollDistance = HARDGOODS_INFINITE_SCROLL_CONSTANTS.INFINITESCROLLDISTANCE;
    this.throttle = HARDGOODS_INFINITE_SCROLL_CONSTANTS.INFINITESCROLLTHROTTLE;
    this.windowContainer = this.windowRefService.nativeWindow();
    this.infiniteScrollContainer = this.scrollListenerService.getInfiniteScroll();
    // this.channelWeb = SETTINGS.CHANNEL.toLocaleLowerCase() === HARDGOODS_CONSTANTS.WEB;
    // this.isRetail = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL;
    this.disableCompareLink = true;
    this.pageHeading = this.selectedDeviceForCompatibleAccessories = '';
    this.isACDP = false;
    this.priceRangeValue = HARDGOODS_CONSTANTS.PRICERANGE;
    this.pageNumber = HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER;
    this.currentCategoriesOnPage = 0;
    this.showDevices = true;
    this.toggleDetails = [];
    this.deviceIndex = 0;
    this.deviceRowIndex = -1;
    if (this.isAccessory) {
      this.disableScroll = true;
      this.findCompatibleAccessoriesFormControl = new UntypedFormControl();
      this.searchResultsForDeviceProductOfferingId = new Map<string, string>();
    }
    // this.showUpdatedBrowseDevices = this.isRetail;
    const postTillStatus = this.stateStoreUtilsService.getPOSVirtualTillStatus();
    this.isStoreOpen = !(
      SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL &&
      (!this.stateStoreUtilsService.getStoreStatus() || !postTillStatus)
    );
    this.userService.p10CustomerLoggedIn$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: boolean) => {
        this.isP10MCustomer = data;
      });
    this.isAuthenticated = false;
  }

  /**
   * Checks for data and propogates modal
   * @returns void
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.repDashwindow = this.windowRefService.nativeWindow();
    // if (!this.channelWeb) {
    //   this.repDashwindow.RepdashEvent.dispatchEvent(new CustomEvent('addDealerCode'));
    // }
    this.getConfigurableData();
    this.sharedBrowseService.selectedRoute = this.router.url;
    this.showDevices = this.router.url.includes(HARDGOODS_CONSTANTS.BROWSE_DEVICES);
    this.subscribeToQueryParams();
    this.modalData = new EntryModal();
    if (this.isAccessory) {
      this.addAccessoriesSection();
      this.accessoryFilterSort();
      this.findCompatibleAccessories();
      if (this.router.url.split('?')[0] === HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY) {
        if (this.stateStoreUtilsService.getAccessoryDetails()) {
          this.findCompatibleAccessoriesFormControl.setValue(
            this.stateStoreUtilsService.getAccessoryDetails()
          );
          this.searchResultsDevice(this.stateStoreUtilsService.getAccessoryDetails());
          this.stateStoreUtilsService.clearAccessoryDetails();
        }
      }
    }
    this.subscribeToRouter();
    this.stateStoreUtilsService.setServiceZeroDollarFlag(false);
    this.isAuthenticated = !!this.stateStoreUtilsService.getAuthCustomerId();
  }
  /**
   * Cleans component on lifecycle hook
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.browseHardgoodsService.setFilters = null;
    this.disableScroll = true;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public setDeviceDetailsIndex(details: ProductDescription, familyIndex: number): void {
    this.deviceIndex = details.index;
    this.deviceDescription = { ...details.deviceDetails };
    this.deviceRowIndex = familyIndex;
  }
  /**
   * Method to set API Error from retail-browser-hardgood component
   * @return void
   */
  public setAPIError(error: APIError[]): void {
    this.errorMessage = error;
  }
  /**
   * Method to update plan source on click
   * @return void
   */
  public onClick(event: boolean): void {
    this.toggleDetails = [];
    this.updateToggleIndexes();
    this.showDevices = !event;
    if (event) {
      this.location.replaceState(HARDGOODS_CONSTANTS.BROWSE_TABLET);
    } else {
      this.location.replaceState(HARDGOODS_CONSTANTS.BROWSE_DEVICES_URL);
    }
    this.getConfigurableData();
    this.sharedBrowseService.selectedRoute = event
      ? HARDGOODS_CONSTANTS.BROWSE_TABLETS
      : HARDGOODS_CONSTANTS.BROWSE_DEVICES;
    this.subscribeToQueryParams();
    this.modalData = new EntryModal();
    this.subscribeToRouter();
    this.pageHeading = !this.showDevices
      ? this.objectService.get(this.contentData, 'browseTabletPageHeading')
      : this.objectService.get(this.contentData, 'browseDevicepageHeading');
  }

  /**
   * selectedFilterCriteria the click events on checkBox
   * @param filter is array of filters
   * @returns void
   */
  public selectedFilterCriteria(filter: Filter[]): void {
    this.filters = filter;
    this.pageNumber = HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER;
    this.currentCategoriesOnPage = 0;
    this.browseHardgoodsService.setFilters = this.filters;
    this.getHardgoodsData();
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.BROWSE_DEVICE_LANDING_COMPONENT,
      'leaving selectedFilterCriteria() in ' +
        HARDGOODS_MODULE_COMPONENTS.BROWSE_DEVICE_LANDING_COMPONENT
    );
  }

  public setDisplayFilter(filterData: UserDisplayFilter[]): void {
    this.userDisplayFilter = filterData;
  }

  /**
   * Method used to get list of hardgoods from API and store in class variable
   * @returns void
   */
  public getHardgoodsData(): void {
    this.isLoading = this.showComponentSpinner = this.disableScroll = true;
    const offerId = this.stateStoreUtilsService.getOfferId();
    const productDetails = this.stateStoreUtilsService.getProductDetails(offerId);
    if (!this.isGlobalSearch && !this.bannerAdDesc) {
      this.browseHardgoodsService
        .loadHardgoodsData(
          this.sharedBrowseService.selectedRoute,
          this.pageNumber,
          this.isUpgrade,
          this.selectedCategory,
          this.selectedDeviceForCompatibleAccessories
        )
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe(
          (hardgoods: HardgoodsDeviceDetails) => {
            if (this.pageNumber === HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER) {
              this.hardGoods = [];
              this.hardGoodsResponse = {};
            }
            this.errorMessage = null;
            if (!hardgoods) {
              this.showModal = true;
              const time = 5;
              setTimeout(() => {
                this.modalData.toggleModalEmitter.emit({ open: true });
              }, time);
            } else {
              this.showComponentSpinner = false;
              // if (this.channelWeb && (this.router.url.split('?')[0] !== HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY)
              // && hardgoods.filter) {
              if (
                this.router.url.split('?')[0] !== HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY &&
                hardgoods.filter
              ) {
                const filterOptions = [];
                hardgoods.filter.forEach((webFilters: Filter) => {
                  if (
                    this.objectService.get(webFilters, 'fieldName', '').toLocaleLowerCase() ===
                      HARDGOODS_CONSTANTS.MANUFACTURER ||
                    this.objectService.get(webFilters, 'fieldName', '').toLocaleLowerCase() ===
                      HARDGOODS_CONSTANTS.PRICERANGE ||
                    this.objectService.get(webFilters, 'fieldName', '').toLocaleLowerCase() ===
                      HARDGOODS_CONSTANTS.FEATURES
                  ) {
                    filterOptions.push(webFilters);
                  }
                });
                this.filters = filterOptions;
              } else {
                this.filters = this.objectService.get(hardgoods, 'filter');
              }

              const sortOpions = this.objectService.get(hardgoods, 'sort');

              if (JSON.stringify(this.sorts) !== JSON.stringify(sortOpions)) {
                this.sorts = this.objectService.get(hardgoods, 'sort');
              }
              this.hardGoodsResponse = hardgoods;
              this.hardGoods = this.hardGoods.concat(
                this.objectService.get(hardgoods, 'productModels')
              );
              this.updateToggleIndexes();
              if (this.isAccessory) {
                this.processHardgoodResponseForAccessory(hardgoods);
              } else {
                this.disableScroll = !hardgoods.isProductAvailable;
              }
            }
            this.configDataLoaded = true;
            this.isLoading = false;
          },
          (error: APIError[]) => {
            this.errorMessage = error;
            this.configDataLoaded = true;
            this.showComponentSpinner = false;
            this.isLoading = false;
            if (this.isAccessory) {
              this.browseHardgoodsService.hardGoodDataSubject.next({});
            }
          }
        );
    } else {
      let globalSearchURL;
      let bannerURL;
      bannerURL = !this.isGlobalSearch
        ? this.bannerAdDesc
        : this.objectService.get(productDetails, 'offerId');
      globalSearchURL = !this.isGlobalSearch
        ? null
        : this.objectService.get(productDetails, 'canonicalUrl');
      this.productDetailService
        .getProductDetailsForAdBanner(bannerURL, globalSearchURL)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (response: Product) => {
            if (response) {
              this.hardGoods = [
                new BrowseDeviceModel(response, this.objectService, SETTINGS.EXT_IMAGE_URL),
              ];
              this.updateToggleIndexes(true);
            }
            this.configDataLoaded = true;
            this.isLoading = false;
            this.showComponentSpinner = false;
          },
          (error: APIError[]) => {
            this.errorMessage = error;
            this.configDataLoaded = true;
            this.showComponentSpinner = false;
            this.isLoading = false;
          }
        );
    }
  }
  private processHardgoodResponseForAccessory(hardgoods: HardgoodsDeviceDetails): void {
    this.getTotalResultsCount(hardgoods);
    if (this.objectService.get(hardgoods, 'filter')) {
      const newCategories = hardgoods.filter.find((filterCategory: Filter) => {
        return filterCategory.displayName.toLowerCase() === HARDGOODS_CONSTANTS.SUBCATEGORY;
      });
      // To get new categories with every filter click
      const categoryFilter = this.filters.find((item: Filter) => {
        return item.displayName.toLowerCase() === HARDGOODS_CONSTANTS.CATEGORY;
      });
      if (categoryFilter && newCategories) {
        const idx = this.filters.indexOf(categoryFilter);
        this.filters[idx] = newCategories;
      }
    }
    const productModels = this.objectService.get(hardgoods, 'productModels');
    if (this.addPreviousCategoryData) {
      this.hardGoodsResponse.productModels = this.hardGoods;
    } else {
      this.addPreviousCategoryData = true;
    }
    let showSeeMoreLink = !(
      this.currentCategoriesOnPage >=
      Number(this.objectService.get(hardgoods, 'metadata.totalRecordCount'))
    );
    showSeeMoreLink = productModels && productModels.length ? showSeeMoreLink : false;
    this.seeMoreAccessories(this.filters, showSeeMoreLink);
    this.browseHardgoodsService.hardGoodDataSubject.next(this.hardGoodsResponse);
  }

  /**
   * Method used to see more accessories and hide see more if filter selected is category
   * @param filters Filter[]
   * @returns void
   */
  public seeMoreAccessories(filters: Filter[], showSeeMoreLink?: boolean): void {
    let showSeeMore = true;
    const filterCateogry = filters.filter((filter: Filter) => {
      return filter.displayName.toLowerCase() === HARDGOODS_CONSTANTS.CATEGORY;
    });
    if (filterCateogry && filterCateogry.length) {
      const filterOption = filterCateogry[0].filterOptions.filter(
        (categoryFilterOption: FilterOption) => {
          return categoryFilterOption.isSelected;
        }
      );
      if (filterOption && filterOption.length) {
        showSeeMore = !(filterOption && filterOption.length);
      } else {
        showSeeMore = showSeeMoreLink;
      }
    }
    this.stateStoreUtilsService.setShowSeeMore(showSeeMore);
  }

  /**
   * Method used to get total results count when filters are applied
   * @param hardgoods HardgoodsDeviceDetails
   * @returns void
   */
  private getTotalResultsCount(hardgoods: HardgoodsDeviceDetails): void {
    this.resultsCount = 0;
    this.currentCategoriesOnPage =
      this.currentCategoriesOnPage +
      Number(this.objectService.get(hardgoods, 'productModels.length'));
    for (const hardgood of hardgoods.productModels) {
      const products = this.objectService.get(hardgood, 'products');
      if (products) {
        const length: number = products.length;
        this.resultsCount = this.resultsCount + length;
      }
    }
  }

  /**
   * Makes API call after reaching to bottem of page to get next 24 values
   */
  public onScroll(): void {
    this.pageNumber++;
    this.getHardgoodsData();
  }

  /**
   * Method used to update devices in case of credit estimation change
   * @returns void
   */
  public getHardgoodsDataOnEventChange(event: { changeId: string }): void {
    if (event && event.changeId === SHARED_BROWSE_CONSTANTS.creditLevelChanged) {
      this.sharedBrowseService.announceBrowsePriceUpdate();
    } else {
      this.getHardgoodsData();
    }
  }

  /**
   * Method used to apply sort
   * @param event contains sort criterion to apply
   * @returns void
   */
  public updateSortRecords(event: SortRecord): void {
    if (event && event.sortCriterion) {
      this.sortApplied = true;
    }
    this.sortCriterion = event.sortCriterion;
    this.sortDisplay = event.sortDisplay;
    this.selectedSort = event.sortCriterion;
    this.browseHardgoodsService.selectedsortOptionValue = event.sortCriterion;
    this.pageNumber = HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER;
    this.isClearSortFilter = false;
    this.getHardgoodsData();
  }

  /**
   * Method used to remove one filter at a time
   * @returns void
   */
  public removeSort(): void {
    this.sortApplied = false;
    this.isClearSortFilter = true;
    this.pageNumber = HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER;
    this.browseHardgoodsService.selectedsortOptionValue = HARDGOODS_CONSTANTS.RELEVANCE;
    this.getHardgoodsData();
    this.selectedSort = '';
  }

  /**
   * Method used to remove all data on change of zip code
   * @returns void
   */
  public clearData(): void {
    this.browseHardgoodsService.filteredApiData =
      this.hardGoods =
      this.browseHardgoodsService.sortedApiData =
        [];
    this.browseHardgoodsService.previousSelectedValues = this.previousSelectedValues = [];
    this.pageNumber = this.pageNumber = HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER;
  }
  /**
   * Method will trigger when checkbox click happens
   * @param value
   */
  public onCheckboxClick(value?: SelectedProductInfo): void {
    if (value) {
      this.sharedBrowseService.setProductDetails(
        value.hardgood,
        value.isCheckBoxSelected,
        value.color
      );
    }
    switch (this.commonUtilService.checkDevice()) {
      case TEXT_CONSTANTS.DEVICE_DESKTOP:
      case TEXT_CONSTANTS.DEVICE_TABLET:
        const MAX_SELECTED_DEVICES_FOR_DESKTOP = 5;
        this.checkDisableLink(MAX_SELECTED_DEVICES_FOR_DESKTOP);
        break;
      case TEXT_CONSTANTS.DEVICE_MOBILE:
        const MAX_SELECTED_DEVICES_FOR_MOBILE = 3;
        this.checkDisableLink(MAX_SELECTED_DEVICES_FOR_MOBILE);
        break;
      default:
    }
  }

  /**
   * Method used to set filters
   * @returns void
   */
  public setFilters(): void {
    this.browseHardgoodsService.setFilters = this.filters;
    this.getHardgoodsData();
  }

  /**
   * Method used to show  filters if filters are set
   * @returns boolean
   */
  public showFilters(): boolean {
    let showFilters = false;
    if (this.filters) {
      for (const filter of this.filters) {
        for (const option of filter.filterOptions) {
          if (option.isSelected) {
            showFilters = true;
            break;
          }
        }
      }
    }
    return showFilters;
  }

  /**
   * Method used to clear all filters on clearAll click
   * @returns void
   */
  public clearAllFilters(): void {
    for (const filter of this.filters) {
      for (const option of filter.filterOptions) {
        option.isSelected = false;
      }
    }
    this.userDisplayFilter = [];
    this.browseHardgoodsService.setFilters = this.filters;
    this.getHardgoodsData();
  }

  /**
   * Method used to navigate to Cdp.
   * @returns void
   */
  public navigateToCdp(category: string): void {
    this.router.navigate([HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_ACDP], {
      queryParams: { category: category },
    });
  }
  /**
   * Method used to clear the filter clicked
   * @returns void
   */
  public clearFilter(filterOption: UserDisplayFilter, index: number): void {
    for (const filter of this.filters) {
      for (const option of filter.filterOptions) {
        if (option.value === filterOption.optionValue) {
          option.isSelected = false;
        }
      }
    }
    this.userDisplayFilter.splice(index, 1);
    this.userDisplayFilter = [].concat(this.userDisplayFilter);
    this.browseHardgoodsService.setFilters = this.filters;
    this.getHardgoodsData();
  }

  private updateToggleIndexes(isGlobalSearch?: boolean): void {
    this.toggleDetails = [];
    if (this.hardGoods && this.hardGoods.length) {
      for (let i = 0; i < this.hardGoods.length; i++) {
        this.toggleDetails.push(this.hardGoods.length === 1 && isGlobalSearch ? true : false);
      }
    }
  }

  /**
   * search results for device product offering id
   */
  private searchResultsDevice(device: string): void {
    if (!!this.selectedDeviceForCompatibleAccessories && device === '') {
      this.selectedDeviceForCompatibleAccessories = device;
      this.pageNumber = HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER;
      this.getHardgoodsData();
    } else if (this.searchResultsForDeviceProductOfferingId.get(device)) {
      this.selectedDeviceForCompatibleAccessories =
        this.searchResultsForDeviceProductOfferingId.get(device);
      this.pageNumber = HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER;
      this.getHardgoodsData();
    } else {
      if (!this.stateStoreUtilsService.getAccessoryOfferId()) {
        const searchLength = this.objectService.get(
          this.contentData,
          'maxSearchLength',
          HARDGOODS_INFINITE_SCROLL_CONSTANTS.SEARCH_LENGTH
        );
        this.browseHardgoodsService
          .searchProductOfferingIdsForSearchedDevice(device, searchLength)
          .subscribe((productOfferingIds: DeviceProductOfferingIdSearchResults[]) => {
            this.deviceSearchResultsAutoCompleteInput = productOfferingIds.map(
              (productOfferingId: DeviceProductOfferingIdSearchResults) => {
                this.searchResultsForDeviceProductOfferingId.set(
                  productOfferingId.productName,
                  productOfferingId.productOfferingID
                );
                return { key: productOfferingId.productName };
              }
            );
            if (this.stateStoreUtilsService.getAccessoryDetails()) {
              this.findCompatibleAccessoriesFormControl.setValue(
                this.stateStoreUtilsService.getAccessoryDetails()
              );
              this.stateStoreUtilsService.clearAccessoryDetails();
            }
          });
      }
    }
  }
  /**
   * Method used to get AEM content
   */
  private getConfigurableData(): void {
    const url = this.getCurrentURL();
    this.browseHardgoodsService
      .getConfigurableContent(url)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((contentData: Object) => {
        this.contentData = this.objectService.get(contentData, 'configurableData');
        this.authorableData = this.objectService.get(contentData, 'authorableData') || [];
        this.logger.debug(
          HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
          HARDGOODS_MODULE_COMPONENTS.BROWSE_DEVICE_LANDING_COMPONENT,
          'loaded hardgood content in ' +
            HARDGOODS_MODULE_COMPONENTS.BROWSE_DEVICE_LANDING_COMPONENT,
          ' contentData: ' + JSON.stringify(this.contentData)
        );
        this.legalText = this.authorableData.filter((legalValue: AemAuthorableData) => {
          return legalValue.uiComponent === HARDGOODS_MODULE_COMPONENTS.LEGAL_TEXT;
        });
        this.showComponentSpinner = false;
        this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'pageLoad');
      });
  }

  /**
   * Method used to disable checkbox and compare link
   * @param maxSelectedCompareDevices is used to allow max limit to select
   */
  private checkDisableLink(maxSelectedCompareDevices: number): void {
    const selectedCompareDevices = this.sharedBrowseService.getProductDetailsLength();
    if (selectedCompareDevices >= maxSelectedCompareDevices) {
      this.disableCheckbox = true;
    } else if (selectedCompareDevices <= 1) {
      this.disableCompareLink = true;
      this.disableCheckbox = false;
    } else {
      this.disableCompareLink = false;
      this.disableCheckbox = false;
    }
  }
  /**
   * Method to subscribe Router events
   * @returns void
   */
  private subscribeToRouter(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntil(this.ngUnsubscribe.asObservable())
      )
      .subscribe((event: NavigationStart) => {
        this.setFilter(this.router.url.split('?')[0]);
        this.clearAccessoryOfferId();
      });
    // QC619445
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.ngUnsubscribe.asObservable())
      )
      .subscribe((event) => {
        const url = this.getCurrentURL();
        if (
          url === HARDGOODS_CONSTANTS.BROWSE_DEVICES_URL ||
          url === HARDGOODS_CONSTANTS.BROWSE_TABLET
        ) {
          this.browseHardgoodsService
            .getConfigurableContent(url)
            .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
            .subscribe((contentData: Object) => {
              // tracking pdl data
            });
        }
      });
  }

  /**
   * Method to set filter
   * @returns void
   */
  private setFilter(page: string): void {
    if (this.filters) {
      this.stateStoreUtilsService.setAccessoryFilter(
        {
          filter: JSON.parse(JSON.stringify(this.filters)),
          displayFilter: JSON.parse(JSON.stringify(this.userDisplayFilter)),
        },
        page
      );
    }
  }

  /**
   * Method to subscribe query params events
   * @returns void
   */
  private subscribeToQueryParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((queryParams: BrowsePhonesQueryParams) => {
        this.isLineInFocus = this.objectService.get(queryParams, 'allowEdit');
        this.isUpgrade = this.objectService.get(queryParams, 'isUpgrade');
        this.isEditingUpgradeLine = this.objectService.get(queryParams, 'isEditingUpgradeLine');
        this.isEditingNonLineDevice = this.objectService.get(queryParams, 'isEditingNonLineDevice');
        /* tslint:disable: triple-equals*/
        this.isGlobalSearch =
          this.isGlobalSearch == undefined
            ? this.objectService.get(queryParams, 'globalSearch')
            : this.isGlobalSearch
              ? !this.isGlobalSearch
              : this.isGlobalSearch;
        this.hardGoodsResponse = null;
        this.bannerAdDesc =
          this.bannerAdDesc == undefined ? this.objectService.get(queryParams, 'bannerAdDesc') : '';
        switch (this.router.url.split('?')[0]) {
          case HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY:
            this.subscribeToAccessoryCategoryQueryParam();
            break;
          case HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY_DETAILS:
            this.addPreviousCategoryData = false;
            this.subscribeToAccessoryCategoryDetailQueryParam(queryParams);
            break;
          case HARDGOODS_CONSTANTS.BROWSE_DEVICES:
            this.subscribeToBrowseDeviceQueryParam();
            break;
          default:
            this.subscribeToBrowseDeviceQueryParam();
            break;
        }
      });
  }

  /**
   * Function used to process hardgood/ Tablet screens
   */
  private subscribeToBrowseDeviceQueryParam(): void {
    const storedFilter: StoredFilter = this.stateStoreUtilsService.getAccessoryFilter(
      this.router.url
    );
    this.userDisplayFilter = JSON.parse(
      JSON.stringify(this.objectService.get(storedFilter, 'displayFilter', []))
    );
    this.selectedFilterCriteria(this.objectService.get(storedFilter, 'filter'));
  }

  /**
   * To process accessory category screen
   */
  private subscribeToAccessoryCategoryQueryParam(): void {
    if (this.stateStoreUtilsService.getAccessoryOfferId()) {
      this.selectedDeviceForCompatibleAccessories =
        this.stateStoreUtilsService.getAccessoryOfferId();
    }
    this.isACDP = false;
    this.selectedCategory = '';
    this.pageHeading = '';
    this.deviceSearchResultsAutoCompleteInput = [];
    const storedFilter: StoredFilter = this.stateStoreUtilsService.getAccessoryFilter(
      this.router.url
    );
    this.userDisplayFilter = JSON.parse(
      JSON.stringify(this.objectService.get(storedFilter, 'displayFilter', []))
    );
    this.selectedFilterCriteria(this.objectService.get(storedFilter, 'filter'));
  }

  /**
   * Process accessory category detail screen
   * @param {BrowsePhonesQueryParams} queryParams
   */
  private subscribeToAccessoryCategoryDetailQueryParam(
    queryParams?: BrowsePhonesQueryParams
  ): void {
    this.pageHeading = this.objectService.get(queryParams, 'category', '');
    this.selectedCategory = this.objectService.get(queryParams, 'category');
    this.isACDP = true;
    if (this.filters) {
      const selectedCatagory = this.router.routerState.snapshot.root.queryParams.category || '';
      this.userDisplayFilter = this.userDisplayFilter.filter((filter: UserDisplayFilter) => {
        return (
          this.objectService.get(filter, 'filterType', ' ').toLowerCase() !==
          HARDGOODS_CONSTANTS.CATEGORY
        );
      });
      this.filters.forEach((filter: Filter, index: number) => {
        if (this.objectService.get(filter, 'displayName', ' ').toLowerCase === 'category') {
          filter.filterOptions.forEach((item: FilterOption) => {
            item.isSelected = item.value === selectedCatagory;
          });
        }
      });
      this.browseHardgoodsService.setFilters = this.filters;
    } else {
      const ACDPFilter = this.stateStoreUtilsService.getAccessoryFilter(
        this.router.url.split('?')[0]
      );
      this.filters = ACDPFilter.filter;
      this.userDisplayFilter = ACDPFilter.displayFilter;
      if (ACDPFilter.displayFilter.length > 0) {
        this.browseHardgoodsService.setFilters = this.filters;
      }
    }
    if (this.selectedCategory) {
      this.getHardgoodsData();
    }
  }
  /**
   * Method to find compatible accessories
   * @returns void
   */
  private findCompatibleAccessories(): void {
    const debounceTimeMS = 600;
    const minLen = 3;
    this.currentCategoriesOnPage = 0;
    this.findCompatibleAccessoriesFormControl.valueChanges
      .pipe(debounceTime(debounceTimeMS), takeUntil(this.ngUnsubscribe))
      .subscribe((device: string) => {
        if (device.length >= minLen) {
          this.searchResultsDevice(device);
        } else if (!device.length) {
          this.deviceSearchResultsAutoCompleteInput = [];
          this.stateStoreUtilsService.clearAccessoryOfferId();
          this.searchResultsDevice(device);
        }
      });
  }
  /**
   * Method called when we navigate bac from add-accessories link
   * @returns void
   */
  private addAccessoriesSection(): void {
    const currentUrl =
      this.router.url.indexOf('?') > -1
        ? this.router.url.slice(0, this.router.url.indexOf('?'))
        : this.router.url;
    if (currentUrl !== HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY_DETAILS) {
      if (currentUrl === HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY) {
        this.isAccessory = true;
        this.disableScroll = true;
        this.sharedBrowseService.selectedRoute = HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES;
      }
    } else {
      this.configDataLoaded = true;
      this.disableScroll = true;
    }
  }
  /**
   * Method to filter accessory
   * @returns void
   */
  private accessoryFilterSort(): void {
    const path = this.router.url.split('?')[0];
    if (
      path === HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES ||
      path === HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY
    ) {
      this.disableScroll = true;
      this.browseHardgoodsService.loadAcccessoryCategoryObservable$
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((loadHardGoods: boolean) => {
          if (loadHardGoods) {
            this.onScroll();
          } else {
            this.browseHardgoodsService.hardGoodDataSubject.next(this.hardGoodsResponse);
          }
        });
    }
  }
  /**
   * method to clear global error messages on child route change
   * @returns void
   */
  public onActivate(): void {
    this.globalErrorService.clearGlobalErrorMessage();
  }
  /**
   * method to clear clear offerId if present
   * @returns void
   */
  public clearAccessoryOfferId(): void {
    if (this.stateStoreUtilsService.getAccessoryOfferId()) {
      this.stateStoreUtilsService.clearAccessoryOfferId();
    }
  }
  /**
   * method to get current url
   * @returns string
   */
  private getCurrentURL(): string {
    let url = this.router.url.split('?')[0];
    switch (url) {
      case HARDGOODS_CONSTANTS.BROWSE_DEVICES:
        url = HARDGOODS_CONSTANTS.BROWSE_DEVICES_URL;
        break;
      case HARDGOODS_CONSTANTS.BROWSE_TABLETS:
        url = HARDGOODS_CONSTANTS.BROWSE_TABLET;
        break;
      default:
        url = HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES_URL;
        break;
    }
    return url;
  }

  /**
   * Navigate to Landing page
   */
  public navigateHome(): void {
    const route = this.stateStoreUtilsService.getCustomerId()
      ? HARDGOODS_CONSTANTS.ACCOUNT_LANDING
      : this.browseHardgoodsService.getHomePage();
    if (route.match(new RegExp(SHARED_BROWSE_CONSTANTS.HTTP_URL, 'i'))) {
      window.location.href = route;
    } else {
      this.router.navigate([route]);
    }
  }
}
